import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { apiClient } from "@/api/api";
import { toast } from "sonner";
import { 
  Card, 
  CardBody, 
  CardFooter, 
  Button, 
  Progress, 
  Snippet, 
  Chip, 
  Tooltip,
  Divider,
  Badge
} from "@heroui/react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";

// Icon components for cleaner JSX
const CloudUploadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
  </svg>
);

const CheckBadgeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-12 h-12">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const AlertIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-12 h-12">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
  </svg>
);

const PrinterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 6.75h10.5M6.75 6.75a2.25 2.25 0 012.25-2.25h6a2.25 2.25 0 012.25 2.25v2.25m-10.5 0v10.5m0-10.5h10.5m0 0v10.5m0-10.5a2.25 2.25 0 012.25 2.25v6a2.25 2.25 0 01-2.25 2.25H18m0 0h-10.5m-3-6h2.25m10.5 0h2.25" />
  </svg>
);

const CertificateValidator: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  
  // New state for advanced loading visuals
  const [uploadProgress, setUploadProgress] = useState(0);
  const [statusText, setStatusText] = useState("Initializing...");

  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#0f766e', '#14b8a6', '#f0fdf4']
    });
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setLoading(true);
    setError(null);
    setResult(null);
    setUploadProgress(0);

    // Simulated progress steps for better UX
    setStatusText("Scanning document structure...");
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 90) return prev;
        return prev + 10;
      });
    }, 200);

    const formData = new FormData();
    formData.append("certificate", file);

    try {
      // Simulate a brief delay to show the animations (optional, remove if not desired)
      await new Promise(r => setTimeout(r, 800)); 
      setStatusText("Verifying cryptographic signature...");

      // Explicitly unset Content-Type so the browser sets it with the boundary for FormData
      const response = await apiClient.post("/verify/certificate", formData, {
        headers: {
          "Content-Type": undefined,
        },
      });

      clearInterval(interval);
      setUploadProgress(100);
      
      setTimeout(() => {
        setResult(response.data);
        if (response.data.valid) {
          toast.success("Certificate Verified Successfully");
          triggerConfetti();
        } else {
          toast.error("Verification Failed");
        }
        setLoading(false);
      }, 500);

    } catch (err: any) {
      clearInterval(interval);
      console.error(err);
      const msg = err.response?.data?.message || "Error verifying document";
      setError(msg);
      toast.error(msg);
      setLoading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
    },
    multiple: false,
    maxSize: 5242880, // 5MB limit
  });

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen w-screen relative flex flex-col items-center justify-center p-6 overflow-hidden bg-gray-50">
      {/* Animated Background Mesh */}
      <div className="absolute inset-0 z-0 opacity-40">
         <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-teal-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
         <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-emerald-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
         <div className="absolute bottom-[-20%] left-[20%] w-[500px] h-[500px] bg-cyan-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      <div className="max-w-3xl w-full z-10">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <Chip variant="flat" color="secondary" className="mb-4 text-xs font-semibold tracking-wider uppercase">
            Official Registry
          </Chip>
          <h1 className="text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-900 to-emerald-700 mb-3 tracking-tight">
            Certificate Validation
          </h1>
          <p className="text-gray-500 max-w-lg mx-auto text-lg leading-relaxed">
            Securely verify Sila Green Agro digital certificates against our immutable blockchain registry.
          </p>
        </motion.div>

        <Card className="w-full bg-white/80 backdrop-blur-xl shadow-2xl border border-white/50 rounded-3xl overflow-hidden">
          {loading && (
             <Progress 
                size="sm" 
                isIndeterminate={uploadProgress < 100} 
                value={uploadProgress} 
                color="secondary" 
                aria-label="Loading..." 
                className="w-full absolute top-0 z-50" 
             />
          )}
          
          <CardBody className="p-0">
            <AnimatePresence mode="wait">
              {!result && !error ? (
                <motion.div
                  key="upload"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="p-12"
                >
                  {loading ? (
                    <div className="flex flex-col items-center justify-center py-12 space-y-6">
                      <div className="relative">
                        <div className="w-20 h-20 rounded-full border-4 border-teal-100 border-t-teal-600 animate-spin"></div>
                        <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-teal-700">
                          {uploadProgress}%
                        </div>
                      </div>
                      <div className="text-center space-y-2">
                        <h3 className="text-xl font-bold text-gray-800">Processing Document</h3>
                        <p className="text-gray-500 animate-pulse">{statusText}</p>
                      </div>
                    </div>
                  ) : (
                    <div
                      {...getRootProps()}
                      className={`relative group border-2 border-dashed rounded-2xl p-16 text-center cursor-pointer transition-all duration-300 ease-in-out ${
                        isDragActive
                          ? "border-teal-500 bg-teal-50/50 scale-[1.02] shadow-inner"
                          : isDragReject 
                          ? "border-red-500 bg-red-50/50"
                          : "border-gray-300 hover:border-teal-400 hover:bg-gray-50/80 hover:shadow-lg"
                      }`}
                    >
                      <input {...getInputProps()} />
                      
                      <div className="relative z-10 flex flex-col items-center gap-6">
                        <motion.div 
                          animate={isDragActive ? { scale: 1.1, y: -10 } : { scale: 1, y: 0 }}
                          className={`w-24 h-24 rounded-full flex items-center justify-center transition-colors ${
                            isDragActive ? 'bg-teal-200 text-teal-800' : 'bg-teal-50 text-teal-600 group-hover:bg-teal-100 group-hover:text-teal-700'
                          }`}
                        >
                          <CloudUploadIcon />
                        </motion.div>
                        
                        <div className="space-y-2">
                          <p className="text-2xl font-semibold text-gray-700">
                            {isDragActive ? "Drop verification file here" : "Upload Digital Certificate"}
                          </p>
                          <p className="text-gray-400">
                            Drag & drop your PDF or <span className="text-teal-600 font-medium hover:underline">browse files</span>
                          </p>
                        </div>

                        <div className="flex items-center gap-2 mt-4">
                          <Chip size="sm" variant="flat" className="bg-gray-200 text-gray-600">PDF Only</Chip>
                          <Chip size="sm" variant="flat" className="bg-gray-200 text-gray-600">Max 5MB</Chip>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              ) : result?.valid ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="p-10"
                >
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    {/* Status Column */}
                    <div className="flex-1 w-full text-center md:text-left space-y-6">
                      <div className="flex flex-col items-center md:items-start">
                        <Badge content="Verified" color="success" size="lg" placement="bottom-right">
                          <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-200 rounded-2xl flex items-center justify-center text-emerald-700 shadow-lg shadow-emerald-100">
                            <CheckBadgeIcon />
                          </div>
                        </Badge>
                      </div>
                      
                      <div>
                        <h2 className="text-3xl font-bold text-gray-900">Authentic Document</h2>
                        <p className="text-emerald-600 font-medium mt-1">{result.message}</p>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <Button 
                          color="primary" 
                          variant="shadow"
                          className="font-semibold bg-teal-700 hover:bg-teal-800"
                          onPress={() => { setResult(null); setError(null); }}
                        >
                          Verify Another
                        </Button>
                        <Button 
                          variant="flat" 
                          color="default"
                          startContent={<PrinterIcon />}
                          onPress={handlePrint}
                        >
                          Download Report
                        </Button>
                      </div>
                    </div>

                    {/* Details Column */}
                    <Card className="flex-[1.5] w-full bg-gray-50/50 border border-gray-200/60 shadow-none">
                      <CardBody className="p-6 space-y-6">
                        <div className="grid grid-cols-2 gap-6">
                           <div>
                              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Animal Name</p>
                              <p className="font-bold text-gray-800 text-xl">{result.animal?.name}</p>
                           </div>
                           <div>
                              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Registration ID</p>
                              <Chip color="secondary" variant="dot" className="pl-2 border-none">
                                {result.animal?.gn}
                              </Chip>
                           </div>
                        </div>

                        <Divider className="my-2"/>

                        <div>
                          <div className="flex justify-between items-center mb-2">
                             <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Cryptographic Hash</p>
                             <Tooltip content="This SHA-256 hash uniquely identifies this document.">
                               <span className="cursor-help text-xs text-teal-600">What is this?</span>
                             </Tooltip>
                          </div>
                          
                          <Snippet 
                            symbol="" 
                            variant="flat" 
                            color="default" 
                            className="w-full font-mono text-xs bg-white border border-gray-200 text-gray-600"
                          >
                            {result.animal?.documentHash || "N/A"}
                          </Snippet>
                        </div>
                        
                        <div className="bg-emerald-50 rounded-lg p-3 text-xs text-emerald-800 border border-emerald-100 flex gap-2">
                          <svg className="w-4 h-4 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                          <span>This certificate has been digitally signed by Sila Green Agro and matches the ledger record.</span>
                        </div>
                      </CardBody>
                    </Card>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="error"
                  initial={{ x: 0 }}
                  animate={{ x: [-10, 10, -10, 10, 0] }} // Shake animation
                  transition={{ duration: 0.5 }}
                  className="p-12 text-center"
                >
                  <div className="w-24 h-24 mx-auto bg-red-50 rounded-full flex items-center justify-center text-red-500 mb-6 shadow-red-100 shadow-xl border border-red-100">
                    <AlertIcon />
                  </div>
                  
                  <h2 className="text-3xl font-bold text-red-900 mb-2">Verification Failed</h2>
                  <p className="text-gray-500 max-w-md mx-auto mb-8">
                    {error || result?.message || "The uploaded file does not match any record in our registry."}
                  </p>

                  <Card className="bg-red-50 border-l-4 border-red-500 shadow-none max-w-lg mx-auto mb-8">
                    <CardBody className="text-left py-4">
                       <p className="font-bold text-red-800 text-sm mb-1">Possible Reasons:</p>
                       <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                         <li>The file has been modified or tampered with.</li>
                         <li>The file is corrupted or incomplete.</li>
                         <li>This is not an official Sila Green Agro PDF.</li>
                       </ul>
                    </CardBody>
                  </Card>

                  <Button 
                    size="lg"
                    className="bg-gray-900 text-white font-bold shadow-lg shadow-gray-300"
                    onPress={() => { setResult(null); setError(null); }}
                  >
                    Try Again
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </CardBody>
          
          <CardFooter className="bg-gray-50/80 backdrop-blur-md border-t border-gray-100 justify-between px-8 py-4">
             <div className="flex items-center gap-2 text-xs text-gray-400">
               <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
               System Online
             </div>
             <Link to="/">
               <Button 
                 variant="light" 
                 color="primary" 
                 size="sm" 
                 className="font-semibold"
                 startContent={<span>&larr;</span>}
               >
                 Back to Home
               </Button>
             </Link>
          </CardFooter>
        </Card>
        
        <div className="mt-8 text-center">
           <p className="text-xs text-gray-400">Secured by Blockchain Technology &bull; SHA-256 Encryption</p>
        </div>
      </div>
    </div>
  );
};

export default CertificateValidator;